import { watch } from 'vue';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { getSampleDataSubName } from '@/free/free.utils.js';
import { refreshLocale } from '@/utils/i18n/i18n';
import { setGlobalSessionData, clearGlobalSessionData } from '@/data/session/session-globals';
import { USER_ROLES } from '@/constants';
import { connectToWebSocketServer } from '@/comments/comments.utils';

import {
    alterApplicationAccessControl,
    convertAdminRoleToFlag,
    getUserFromUserData,
    hasGlobalAccess,
    hasAdminAccess,
    isAdmin,
    isV2SubAdmin,
    isAdoptManager,
    isGuest,
    isOps,
    isPendozer,
    isReadOnly,
    isSuper,
    isSupport,
    isUser,
    roleName,
    turnOffSampleData,
    isSampleDataTurnedOn,
    canViewSessionReplayForApp,
    filterAppIdsWithAdminOrAgentAdminAccess,
    hasAdminOrAiAgentAdminAccessForApp,
} from '@/vuex/modules/auth/auth.helpers.js';
import {
    fetchUser,
    fetchUserDetails,
    login,
    logout,
    update,
    updatePassword,
    updateUsername,
    updateEmail,
    fetchUnauthenticatedTrainingAttributes,
} from '@/vuex/modules/auth/auth.utils';
import { getSubIdFromUrlPath } from '@utils/sub-id-routing.utils';
import { http } from '@pendo/http';
import { initPermissions, $canOrg, $can } from '@pendo/permissions';
import { identifyAnonymous, identify } from '@/vuex/plugins/analytics';
import { registerIntercom } from '@/utils/intercom.js';
import {
    localSegmentFlagsExist,
    setSegmentFlags as setLocalSegmentFlags,
    hasSegmentFlag as hasLocalStorageSegmentFlag,
} from '@/data/flags/local-flags';
import { useSegmentFlagOverride } from '@/utils/segment-flag.utils';

export const LOAD_STATUS = {
    NOT_STARTED: 0,
    PENDING: 1,
    COMPLETE: 2,
};

export function getInitialState () {
    return {
        authenticated: false,
        initialized: false,
        hasLoginError: false,
        hasUpdateSettingsError: false,
        redirectUrl: null,
        segmentFlags: [],
        segmentFlagsLoadStatus: LOAD_STATUS.NOT_STARTED,
        user: {},
        userLoadStatus: LOAD_STATUS.NOT_STARTED,
        preSampleDataSubId: null,
        subAccessError: null,
        onlyTrainingSub: false,
        awaitingEmailVerification: false,
        unauthenticatedTrainingAttributes: {},
    };
}

const auth = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setUserLoadStatus (state, { status }) {
            state.userLoadStatus = status;
        },
        setAuthenticated (state, { authenticated }) {
            state.authenticated = authenticated;
        },
        setInitialized (state, { initialized }) {
            state.initialized = initialized;
        },
        setUser (state, { user }) {
            state.user = user;
        },
        setProperty (state, { property, value }) {
            state.user[property] = value;
        },
        setUserSegmentFlags (state, { flags }) {
            setLocalSegmentFlags(flags);
            state.segmentFlags = flags;
        },
        setSegmentFlagsLoadStatus (state, { status }) {
            state.segmentFlagsLoadStatus = status;
        },
        setLoginError (state, { hasLoginError }) {
            state.hasLoginError = hasLoginError;
        },
        setUpdateSettingsError (state, { hasUpdateSettingsError }) {
            state.hasUpdateSettingsError = hasUpdateSettingsError;
        },
        setRedirectUrl (state, { redirectUrl }) {
            state.redirectUrl = redirectUrl;
        },
        setPreSampleDataSubId (state, { id }) {
            state.preSampleDataSubId = id;
        },
        setSubAccessError (state, { error }) {
            state.subAccessError = error;
        },
        reset (state) {
            const { unauthenticatedTrainingAttributes } = state;
            // preserve unauthenticatedTrainingAttributes to persist on logout

            Object.assign(state, getInitialState(), { unauthenticatedTrainingAttributes: unauthenticatedTrainingAttributes || {} });
        },
        setOnlyTrainingSub (state, { onlyTrainingSub }) {
            state.onlyTrainingSub = onlyTrainingSub;
        },
        setAwaitingEmailVerification (state, { awaitingEmailVerification }) {
            state.awaitingEmailVerification = awaitingEmailVerification;
        },
        setUnauthenticatedTrainingAttributes (state, { unauthenticatedTrainingAttributes }) {
            state.unauthenticatedTrainingAttributes = unauthenticatedTrainingAttributes;
        },
    },
    actions: {
        init ({ dispatch }) {
            dispatch('setupSegmentFlagListeners');
            dispatch('getUser');
        },
        setupSegmentFlagListeners ({ commit }) {
            const updateFlags = () => {
                const segmentFlags = window.pendo?.segmentFlags || [];
                commit('setUserSegmentFlags', { flags: segmentFlags });
                commit('setSegmentFlagsLoadStatus', { status: LOAD_STATUS.COMPLETE });
            };

            document.addEventListener('pendo_segmentFlagsReady', updateFlags);
            document.addEventListener('pendo_segmentFlagsUpdated', updateFlags);
            document.addEventListener('pendo_segmentFlagsError', updateFlags);

            if (window.pendo?.segmentFlags) {
                updateFlags();
            }
        },
        async refreshVeltToken ({ commit }) {
            try {
                const { user: { velt = {} } = {} } = await fetchUser();

                commit('setProperty', { property: 'velt', value: velt, reIdentify: false });
            } catch (error) {
                throw error;
            }
        },
        async waitForSegmentFlags ({ commit, state }) {
            return new Promise((resolve) => {
                if (state.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE) return resolve();

                const unwatch = watch(
                    () => state.segmentFlagsLoadStatus,
                    (status) => {
                        if (status === LOAD_STATUS.COMPLETE) {
                            unwatch();
                            resolve();
                        }
                    },
                );

                setTimeout(() => { // Failsafe in case segment flags never seem to load
                    if (state.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE) return;

                    console.warn('Pendo SDK is ready but segment flags have not loaded. This may be due to an issue with the Pendo snippet or network connectivity.'); // eslint-disable-line no-console
                    unwatch();
                    commit('setSegmentFlagsLoadStatus', { status: LOAD_STATUS.COMPLETE });
                    resolve();
                }, 250);
            });
        },
        async waitForUserLoad ({ state }) {
            return new Promise((resolve) => {
                if (state.userLoadStatus === LOAD_STATUS.COMPLETE) return resolve();

                const unwatch = watch(
                    () => state.userLoadStatus,
                    (status) => {
                        if (status === LOAD_STATUS.COMPLETE) {
                            unwatch();
                            resolve();
                        }
                    },
                );
            });
        },
        async getUser ({ commit, getters, rootGetters, dispatch }) {
            commit('setUserLoadStatus', { status: LOAD_STATUS.PENDING });
            let userData;

            try {
                try {
                    userData = await fetchUser();
                } catch (error) {
                    identifyAnonymous();
                    clearGlobalSessionData();
                    refreshLocale();
                    throw error;
                }

                const subscriptionList = get(userData, 'subscriptions', []);
                const sampleDataSubName = getSampleDataSubName();
                const sampleDataSub = subscriptionList.find((sub) => get(sub, 'name', undefined) === sampleDataSubName);
                const sampleDataSubId = get(sampleDataSub, 'id', undefined);
                const sampleDataSubIsActive = sampleDataSubId === get(userData, 'activeSubscription.id');
                const preSampleDataSub = subscriptionList.find(
                    (sub) => get(sub, 'featureFlags.pendoFree', null) && sub.id !== sampleDataSubId,
                );

                if (preSampleDataSub) {
                    // always set the preSampleDataSubId if a pendo-free sub exists
                    commit('setPreSampleDataSubId', { id: preSampleDataSub.id });
                }

                if (isSampleDataTurnedOn() || sampleDataSubIsActive) {
                    if (sampleDataSub && window.location.pathname !== '/post-register-steps') {
                        // Force user to be read-Only
                        // v1 perms
                        userData.user.flags.admin = false;
                        userData.user.flags.readOnly = true;
                        // v2 perms
                        userData.user.applicationAccessControl = {};
                        userData.user.subscriptionAccessControl = [];
                        userData.user.organizationAccessControl = [];
                        userData.activeSubscription.value = sampleDataSubId;
                    } else {
                        turnOffSampleData();

                        // if the pendoFree sub has been upgraded while the user is in demo-mode, default to the first paid sub
                        if (userData.activeSubscription) {
                            const defaultSub = subscriptionList.find((sub) => sub.id !== sampleDataSubId);
                            userData.activeSubscription.value = get(defaultSub, 'id', undefined);
                        }
                    }
                }

                const currentRouteSubId = getSubIdFromUrlPath();
                if (currentRouteSubId && currentRouteSubId !== userData.activeSubscription.id) {
                    try {
                        await http.put(`/api/user/${userData.user.userid}/setsubscription/${currentRouteSubId}`);
                        userData = await fetchUser();
                    } catch (_err) {
                        console.error(`Cannot set active subscription to ${currentRouteSubId}`, _err);
                        commit('setSubAccessError', { error: { id: currentRouteSubId } });
                    }
                }

                const { activeOrganization } = userData;
                const user = getUserFromUserData(userData);

                setGlobalSessionData({ ...userData, user });
                commit('setUser', { user });
                commit('organization/setActive', { activeOrganization }, { root: true });
                commit('setAuthenticated', { authenticated: true });
                await dispatch('hydrate', userData);

                // Initialize Permissions
                let {
                    applicationAccessControl = {},
                    subscriptionAccessControl = {},
                    organizationAccessControl = {},
                } = user;

                // Historically, the backup permission function was used to support both V1 permissions (defined in
                // ad-hoc Vuex utilities and Go API middleware) and V2 permissions (unified entity-action-source-target
                // architecture). Since December 2024, all Pendo subscriptions have been fully migrated to V2
                // permissions. As such, the backup function has been removed to reduce maintenance overhead.

                applicationAccessControl = alterApplicationAccessControl(applicationAccessControl, rootGetters);
                initPermissions({ applicationAccessControl, subscriptionAccessControl, organizationAccessControl });
            } catch (error) {
                commit('setAuthenticated', { authenticated: false });
                commit('setUserLoadStatus', {
                    status: LOAD_STATUS.COMPLETE,
                });

                throw error;
            }

            const userDetailRequests = [
                dispatch('userSettings/loadAll', { reFetch: true }, { root: true }),
                dispatch('getUserDetails'),
            ];

            await Promise.all(userDetailRequests);

            dispatch('globalFilters/loadInitialStateFromUserSettings', null, { root: true });

            // We want everything hydrated before initializing the pendo snippet
            // and identifying the user
            identify();
            registerIntercom(userData.intercomChatJwt);

            commit('setUserLoadStatus', { status: LOAD_STATUS.COMPLETE });

            const hasPendoCollaborationFlag = typeof getters.hasSegmentFlag === 'function'
                ? getters.hasSegmentFlag('pendoCollaboration')
                : hasLocalStorageSegmentFlag('pendoCollaboration');

            if (hasPendoCollaborationFlag) {
                connectToWebSocketServer({ dispatch });
            } else {
                await dispatch('collaboration/initCollaboration', null, { root: true });
            }

            if (window.Cypress) {
                window.authInitReady = true;
            }
        },
        async getUserDetails ({ commit, state }) {
            if (state.onlyTrainingSub) {
                return;
            }
            const {
                first,
                last,
                vendorUserId,
                vendorUserPermissionId,
                jobRole,
                useCase,
                locale,
            } = await fetchUserDetails(state.user.id);

            // reIdentify prop is passed here to avoid reidentifying needlessly
            // within the analytics vuex plugin
            commit('setProperty', { property: 'first', value: first, reIdentify: false });
            commit('setProperty', { property: 'last', value: last, reIdentify: false });
            commit('setProperty', { property: 'jobRole', value: jobRole, reIdentify: false });
            commit('setProperty', { property: 'useCase', value: useCase, reIdentify: false });
            commit('setProperty', { property: 'locale', value: locale, reIdentify: false });

            if (vendorUserId) {
                commit('setProperty', {
                    property: 'vendorUserId',
                    value: vendorUserId,
                });
            }

            if (vendorUserPermissionId) {
                commit('setProperty', {
                    property: 'vendorUserPermissionId',
                    value: vendorUserPermissionId,
                });
            }
        },
        async hydrate ({ dispatch, getters }, { subscriptions, activeSubscription }) {
            await dispatch('subscription/hydrate', { subscriptions, activeSubscription }, { root: true });

            if (!activeSubscription) {
                return;
            }

            const { productLines, applications } = activeSubscription;
            await dispatch('apps/hydrate', { applications }, { root: true });
            await dispatch('productLines/hydrate', { productLines }, { root: true });
            if (getters.hasAdminAccess) {
                await dispatch('aiFlags/hydrate', {}, { root: true });
            }
        },
        updateOnlyTrainingSub ({ commit }, { onlyTrainingSub }) {
            commit('setOnlyTrainingSub', { onlyTrainingSub });
        },
        async login ({ commit }, { username, password, queryString, postLogin = false, data = {} } = {}) {
            try {
                if (!postLogin) {
                    data = await login(username, password, queryString);
                }
                const { redirectUrl } = data;
                commit('setRedirectUrl', { redirectUrl });
            } catch {
                commit('setLoginError', { hasLoginError: true });
            }
        },
        async logout ({ dispatch }) {
            dispatch('pendoCollaboration/teardown', null, { root: true });
            dispatch('collaboration/signOut', null, { root: true });
            await logout();
        },
        async changePassword ({ commit }, { oldPass, newPass }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                await updatePassword(oldPass, newPass);
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async changeEmail ({ commit, getters }, { newUsername }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                await updateEmail(newUsername, getters.userName);
                commit('setAwaitingEmailVerification', {
                    awaitingEmailVerification: true,
                });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async changeUsername ({ commit }, { newUsername, password }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                await updateUsername(newUsername, password);
                commit('setProperty', {
                    property: 'username',
                    value: newUsername,
                });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateFirstName ({ commit, state }, { firstName }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                // convertAdminRoleToFlag mutates
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, first: firstName });
                commit('setProperty', { property: 'first', value: firstName });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateLastName ({ commit, state }, { lastName }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                // convertAdminRoleToFlag mutates
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, last: lastName });
                commit('setProperty', { property: 'last', value: lastName });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUserProperties ({ commit, state }, properties) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, ...properties });

                Object.keys(properties).forEach((key) => {
                    commit('setProperty', { property: key, value: properties[key], reIdentify: true });
                });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUseCase ({ commit, state }, { useCase }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                // convertAdminRoleToFlag mutates
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, useCase });
                commit('setProperty', { property: 'useCase', value: useCase, reIdentify: true });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateJobRoleAndUseCase ({ commit, state }, { useCase, jobRole }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                // convertAdminRoleToFlag mutates
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, useCase, jobRole });
                commit('setProperty', { property: 'useCase', value: useCase, reIdentify: true });
                commit('setProperty', { property: 'jobRole', value: jobRole, reIdentify: true });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async updateUserLocale ({ commit, state }, { locale }) {
            commit('setUpdateSettingsError', { hasUpdateSettingsError: false });
            try {
                // convertAdminRoleToFlag mutates
                const user = convertAdminRoleToFlag(cloneDeep(state.user));
                await update({ ...user, locale });
                commit('setProperty', { property: 'locale', value: locale });
            } catch (error) {
                commit('setUpdateSettingsError', {
                    hasUpdateSettingsError: true,
                });
                throw error;
            }
        },
        async fetchUnauthenticatedTrainingAttributes ({ commit }) {
            const unauthenticatedTrainingAttributes = await fetchUnauthenticatedTrainingAttributes();

            commit('setUnauthenticatedTrainingAttributes', { unauthenticatedTrainingAttributes: unauthenticatedTrainingAttributes || {} });
        },
    },
    // PLEASE keep these getters specific to the currently authenticated user `state.user`
    // if you find that you are creating a getter and passing in a userId as an parameter
    // it probably belongs in the `users` module where we handle user management.
    getters: {
        userId (state) {
            return state.user?.id;
        },
        userName (state) {
            return state.user?.username;
        },
        firstName (state) {
            return state.user?.first;
        },
        lastName (state) {
            return state.user?.last;
        },
        userLocale (state) {
            return state.user?.locale;
        },
        preSampleDataSubId (state) {
            return state.preSampleDataSubId;
        },
        isLoadingSegmentFlags (state) {
            return !localSegmentFlagsExist() && state.segmentFlagsLoadStatus === LOAD_STATUS.PENDING;
        },
        isLoadedSegmentFlags (state) {
            return function () {
                return localSegmentFlagsExist() || state.segmentFlagsLoadStatus === LOAD_STATUS.COMPLETE;
            };
        },
        isAuthenticated (state) {
            return !isEmpty(state.user) && state.authenticated;
        },
        isLoading (state) {
            return state.userLoadStatus === LOAD_STATUS.PENDING;
        },
        // If the `/api/init` call is currently in-flight, we set up a watcher on `isLoaded`
        // before proceeding to check if user is authenticated or not. In order to properly apply
        // our watcher on the store for that piece of state, we return a method here instead of just the state.
        isLoaded (state) {
            return function () {
                return state.userLoadStatus === LOAD_STATUS.COMPLETE;
            };
        },
        hasCompletedUserLoad (state) {
            return state.userLoadStatus === LOAD_STATUS.COMPLETE;
        },
        isPublic (state, getters) {
            return !getters.isAuthenticated;
        },
        isReadOnly: (state) => isReadOnly(state.user),
        isUser: (state) => isUser(state.user),
        isPendozer: (state) => isPendozer(state.user),
        isAdmin: (state) => isAdmin(state.user),
        isAdoptManager (state) {
            return isAdoptManager(state.user);
        },
        isSuper: (state) => isSuper(state.user),
        isSupport: (state) => isSupport(state.user),
        isOps: (state) => isOps(state.user),
        isGuest: (state) => isGuest(state.user),
        isImpersonating: (state, getters, rootState) => {
            if (getters.isGuest) {
                return true;
            }

            if (getters.hasSuperAccess) {
                const { active, map } = rootState.subscription;

                return !map[active.id];
            }

            return false;
        },
        isAuthorizedForRoute: (state, getters) => (accessLevel) => {
            // access level 1 is public page
            if (!accessLevel || accessLevel === 1) {
                return true;
            }

            if (accessLevel === 'OPS') {
                return getters.isOps;
            }

            if (accessLevel === 'ORG_ADMIN') {
                return (
                    $canOrg()
                        .entity('organization')
                        .action('view') || getters.isOps
                );
            }

            return state.user.role >= accessLevel;
        },
        hasUserAccess: (state) => state.user.role >= USER_ROLES.USER,
        hasAdminAccess: (state) => hasAdminAccess(state.user),
        hasV2SubAdminAccess: (state) => isV2SubAdmin(state.user),
        hasGlobalAccess: (state) => hasGlobalAccess(state.user),
        hasSuperAccess: (state) => state.user.role >= USER_ROLES.SUPER,
        hasOpsAccess: (state) => isOps(state.user),
        hasPermission: (state, getters, rootState, rootGetters) => (permission) => {
            // super/ops may or may not have flags set, but have permission regardless
            if (getters.isSuper || getters.isOps) {
                return true;
            }
            // with beta publishing control, user AND admin role flags are editable
            if (rootGetters['subscription/getFeatureFlag']('guidePublishControl') || getters.isUser) {
                return get(state.user, `flags.${permission}`, false);
            }
            // admins without beta publishing control have all permissions
            if (getters.hasAdminAccess) {
                return true;
            }

            return false;
        },
        userCanAccessClassicFeedback: (state, getters, rootState, rootGetters) => {
            const userHasLinkedUserId = get(state.user, 'vendorUserId', false);

            return userHasLinkedUserId && rootGetters['productLines/classicFeedbackEnabled'];
        },
        roleName: (state) => roleName(state.user),
        flagList (state, getters) {
            if (getters.isSuper) {
                return [];
            }

            const { flags } = state.user;
            if (!flags) {
                return;
            }

            return Object.keys(flags).filter((flag) => flags[flag]);
        },
        // Granular guide permission checks
        canEditGuides: (state, getters) => {
            const canEditTemplate = getters.hasPermission('allowGuideEditViaTemplate');
            const canEditSource = getters.hasPermission('allowGuideEditViaSource');

            return !!canEditTemplate || !!canEditSource;
        },
        canEditGuide: (state, getters) => (guide) => {
            if (getters.isReadOnly) {
                return false;
            }

            const isPublishedGuide = guide.state === 'public';
            const guideHasSteps = Array.isArray(guide.steps) && !!guide.steps.length;

            const canEditAnyStep = (steps) => steps.some((step) => getters.canEditStep(step));

            // if guide is NOT public, check if it has any steps
            // if guide has steps, loop through each step and check permission
            // if guide does NOT have steps, return TRUE
            if (!isPublishedGuide) {
                if (guideHasSteps) {
                    return canEditAnyStep(guide.steps);
                }

                return true;
            }

            // guide IS public, check if user has permission to manage published guides
            // if guide has steps, loop through each step and check permission
            // if guide does NOT have steps, return TRUE
            const canManagePublishedGuide = getters.hasPermission('allowGuidePublish');
            if (canManagePublishedGuide) {
                if (guideHasSteps) {
                    return canEditAnyStep(guide.steps);
                }

                return true;
            }

            // if user has failed previous two checks for editing permission, return FALSE
            return false;
        },
        canEditStep: (state, getters) => (step) => {
            if (getters.isReadOnly) {
                return false;
            }

            if (step.templateId) {
                return getters.hasPermission('allowGuideEditViaTemplate');
            }

            return getters.hasPermission('allowGuideEditViaSource');
        },
        // This method only determines if the user has multiple subs and has the correct flags
        // for copy guide/rc to be enabled. It is still important to check if the user has the
        // correct create permission
        canCopyGuidesToSubscription: (state, getters, rootState, rootGetters) => () => {
            const subs = rootGetters['subscription/allSubscriptions'];
            if (subs.length <= 1) return false;

            const hasEnableGuideCopyFlag = rootGetters['subscription/getFeatureFlag']('enableGuideCopy');
            if (rootGetters['subscription/getFeatureFlag']('enableGuideCopyEditRestrictions')) return false;
            if (rootGetters['productLines/hasProductLinePurchased']('adoptEndUser') && hasEnableGuideCopyFlag) return true;

            if (!getters.hasSegmentFlag('copyGuides')) return false;
            if (hasEnableGuideCopyFlag) return true;
            if (rootGetters['productLines/hasProductLinePurchased']('adoptReseller')) return true;

            return false;
        },
        canCopyRCToSubscription: (state, getters, rootState, rootGetters) => () => {
            const subs = rootGetters['subscription/list'];
            if (subs.length <= 1) return false;
            if (!getters.hasSegmentFlag('copyGuides')) return false;
            if (rootGetters['subscription/getFeatureFlag']('enableGuideCopyEditRestrictions')) return false;

            if (rootGetters['subscription/getFeatureFlag']('enableGuideCopy')) {
                return true;
            }

            return false;
        },
        canCreatePages: (state, getters, rootState) => {
            const appMap = rootState.apps.map;
            const canAppCreatePages = (appId) =>
                $can(appId)
                    .entity('page')
                    .action('create');

            return Object.keys(appMap).some(canAppCreatePages);
        },
        appIdsWithSessionReplayRoleEnabled: (state, getters, rootState) => {
            let appIdStrings;

            const appMap = rootState.apps.map;
            if (getters.hasAdminAccess) {
                appIdStrings = Object.keys(appMap);
            } else {
                appIdStrings = Object.keys(appMap).filter(canViewSessionReplayForApp);
            }

            return appIdStrings.map(Number);
        },
        hasAppsWithSessionReplayRoleEnabled: (state, getters) => {
            return getters.appIdsWithSessionReplayRoleEnabled.length > 0;
        },
        appIdsWithAdminOrAgentAdminAccess: (state, getters, rootState) => {
            const appMap = rootState.apps.map;
            const appIds = Object.keys(appMap);

            const { hasAdminAccess, hasSuperAccess, hasV2SubAdminAccess, hasOpsAccess } = getters;

            return filterAppIdsWithAdminOrAgentAdminAccess(appIds, state.user.applicationRoles, {
                hasAdminAccess,
                hasSuperAccess,
                hasV2SubAdminAccess,
                hasOpsAccess,
            });
        },
        hasAppsWithAdminOrAiAgentAdminAccess: (state, getters) => {
            return getters.appIdsWithAdminOrAgentAdminAccess.length > 0;
        },
        accessibleAppIds: (state, getters, rootState) => {
            const appIds = Object.keys(rootState.apps.map).map(Number);
            const { hasAdminAccess, hasSuperAccess, hasV2SubAdminAccess } = getters;

            if (hasAdminAccess || hasSuperAccess || hasV2SubAdminAccess) return appIds;

            const userAppIds = Object.keys(state.user.applicationAccessControl || {}).map(Number);

            return appIds.filter((id) => userAppIds.includes(id));
        },
        hasAdminOrAiAgentAdminAccess: (state, getters) => (appId) => {
            return hasAdminOrAiAgentAdminAccessForApp(getters.appIdsWithAdminOrAgentAdminAccess, appId);
        },
        hasSegmentFlag: (state) => (flag) => {
            if (useSegmentFlagOverride()) {
                return true;
            }

            if (!state.segmentFlags?.length) {
                return hasLocalStorageSegmentFlag(flag);
            }

            return state.segmentFlags.includes(flag);
        },
        hasOnlyTrainingSub (state) {
            return state.onlyTrainingSub;
        },
        userSegmentFlags: (state) => state.segmentFlags,
        hasSessionReplayViewerAccess: (state, getters, rootState, rootGetters) => () => {
            const hasSessionReplayEnabled = rootGetters['subscription/hasSessionReplayEnabled'];
            const isPendoFree = rootGetters['subscription/isPendoFree'];
            const isTrainingSubscription = rootGetters['subscription/isTrainingSubscription'];
            const { hasAppsWithSessionReplayRoleEnabled } = getters;

            const canAccessSessionReplay =
                hasSessionReplayEnabled &&
                !isTrainingSubscription &&
                !isPendoFree &&
                hasAppsWithSessionReplayRoleEnabled;

            return canAccessSessionReplay;
        },
        hasSessionReplayAdminAccess: (state, getters) => () => {
            const { hasAdminAccess, hasSessionReplayViewerAccess } = getters;
            const canAccessSessionReplay = hasSessionReplayViewerAccess() && hasAdminAccess;

            return canAccessSessionReplay;
        },
    },
};

export default auth;
